// ============================================================
// EL WELL™ — SITE CONTENT
// This is the single file to edit when updating the website.
// Search for "EDIT" to find values that may still change
// following final regulatory review.
// ============================================================

export const brand = {
  name: "EL Well",
  tm: "EL Well™",
  tagline: "Wellness, Simply.",
  intro:
    "EL Well began with a conviction: supplements should be made without compromise — products we would confidently give to our own families.",
  email: "hello@elwellnutrition.co.uk",
  domain: "elwellnutrition.co.uk",
  country: "United Kingdom",
};

export const navLinks = [
  { label: "Home", to: "/" },
  { label: "Shop", to: "/shop" },
  { label: "Why EL Well", to: "/why-el-well" },
  { label: "About EL Well", to: "/about" },
  { label: "FAQs", to: "/faqs" },
  { label: "Contact", to: "/contact" },
];

export const marqueeItems = [
  "Simple Formulations",
  "Transparent Ingredients",
  "Quality-Focused",
  "Made for the UK",
  "Nothing to Hide",
  "Wellness, Simply.",
];

export const product = {
  slug: "magnesium-bisglycinate",
  edition: "Nº 01",
  name: "Magnesium Glycinate",
  capsules: 180,
  form: "Capsules",
  bottle: "Premium black bottle",

  // EDIT: final retail price in GBP (current value is a launch placeholder)
  price: 24.99,

  supplyNote: "90-day supply",

  tagline:
    "A simple, considered magnesium formula using 100% magnesium bisglycinate — fully reacted, non-buffered and with no magnesium oxide.",

  highlights: [
    "100% Magnesium Bisglycinate",
    "200 mg Elemental Magnesium per daily serving",
    "Fully reacted & non-buffered",
    "No magnesium oxide",
    "180 vegan capsules · 90-day supply",
  ],

  // Product page checklist (confirmed serving: 2 capsules/day)
  pageHighlights: [
    "100% Magnesium Bisglycinate Chelate",
    "200 mg Elemental Magnesium per daily serving",
    "180 Vegan Capsules",
    "90-Day Supply",
    "No Magnesium Oxide",
  ],

  benefits: {
    intro: "Simple magnesium, clearly explained.",
    body: "EL Well Nº 01 provides 200 mg elemental magnesium per daily serving from magnesium bisglycinate.",
    listTitle: "Magnesium contributes to:",
    items: [
      "the reduction of tiredness and fatigue",
      "normal energy-yielding metabolism",
      "normal muscle function",
      "normal functioning of the nervous system",
      "normal psychological function",
      "electrolyte balance",
    ],
  },

  ingredients: {
    main: "Ingredients: Magnesium Bisglycinate, Capsule Shell: Hydroxypropyl Methylcellulose (HPMC).",
    none: "No magnesium oxide. No excipients.",
    perCapsule: "Each capsule provides 100 mg elemental magnesium from magnesium bisglycinate.",
  },

  directions: {
    dose: "Take 2 capsules daily with water.",
    serving: "Daily serving: 2 capsules",
    warnings: [
      "Do not exceed the stated recommended dose.",
      "A food supplement should not be used as a substitute for a varied, balanced diet and a healthy lifestyle.",
      "Store in a cool, dry place and keep out of reach and sight of children.",
    ],
  },

  nutrition: {
    rows: [
      { label: "Per capsule", value: "Magnesium: 100 mg" },
      { label: "Per daily serving — 2 capsules", value: "Magnesium: 200 mg", nrv: "NRV: 53%" },
      { label: "Source", value: "Magnesium Bisglycinate" },
      { label: "Bottle", value: "180 capsules" },
      { label: "Daily serving", value: "2 capsules" },
      { label: "Supply", value: "90 days" },
    ],
    note: "NRV = Nutrient Reference Value",
  },

  claims: [
    "a reduction of tiredness and fatigue",
    "electrolyte balance",
    "normal energy-yielding metabolism",
    "normal functioning of the nervous system",
    "normal muscle function",
    "normal protein synthesis",
    "normal psychological function",
    "the maintenance of normal bones",
    "the maintenance of normal teeth",
  ],
  claimsExtra: "Magnesium also has a role in the process of cell division.",

  qualityPoints: [
    "Every ingredient declared on the label — nothing hidden",
    "Formulated with a short, deliberate ingredient list",
    "Produced for the UK market with full batch traceability",
  ],

  faqs: [
    {
      q: "Is magnesium glycinate the same as magnesium bisglycinate?",
      a: "Magnesium bisglycinate is often referred to simply as magnesium glycinate. EL Well Nº 01 uses magnesium bisglycinate. We use Magnesium Glycinate as the familiar product name while clearly stating the precise form used in the formulation.",
    },
    {
      q: "What form of magnesium does EL Well Nº 01 use?",
      a: "EL Well Nº 01 uses 100% magnesium bisglycinate as its magnesium form. The formulation is fully reacted, non-buffered and contains no magnesium oxide.",
    },
    {
      q: "Does EL Well Nº 01 contain magnesium oxide?",
      a: "No. EL Well Nº 01 contains no magnesium oxide.",
    },
    {
      q: "How much elemental magnesium is provided?",
      a: "Each capsule provides 100 mg elemental magnesium. The recommended daily serving of 2 capsules provides 200 mg elemental magnesium.",
    },
    {
      q: "How many capsules are in each bottle?",
      a: "Each bottle contains 180 capsules.",
    },
    {
      q: "How long does one bottle last?",
      a: "At the recommended serving of 2 capsules per day, one bottle provides a 90-day supply.",
    },
    {
      q: "Does the formula contain excipients?",
      a: "No. The current product specification lists no excipients.",
    },
    {
      q: "What is the capsule made from?",
      a: "The capsule shell is made from Hydroxypropyl Methylcellulose (HPMC).",
    },
    {
      q: "Why did EL Well choose magnesium bisglycinate?",
      a: "We wanted Nº 01 to reflect the EL Well approach: a simple, clearly identified magnesium form without magnesium oxide or unnecessary active ingredients.",
    },
    {
      // EDIT: the 1,667 mg figure follows the current manufacturer specification —
      // update here after the final regulatory review if required.
      q: "Why 200 mg of elemental magnesium?",
      a: "Many magnesium supplements advertise large numbers, but these often refer to the total weight of the magnesium compound, not the amount of magnesium your body actually receives. EL Well provides 1,667 mg magnesium bisglycinate chelate, delivering 200 mg elemental magnesium — so you know both the total amount of magnesium bisglycinate used and the actual amount your body receives. According to NHS guidance for adults aged 19–64, the recommended daily magnesium intake is 300 mg for men and 270 mg for women, including magnesium from both food and supplements. We believe understanding your supplement is just as important as taking it — because wellness starts with knowing exactly what you're putting into your body.",
    },
  ],
};

export const home = {
  heroEyebrow: "UK Supplements — Est. 2026",
  heroLines: ["Wellness,", "Simply."],
  heroBody:
    "Premium nutrition made simple. Carefully selected ingredients. Transparent formulations. Everyday wellbeing.",
  chapters: [
    {
      number: "01",
      title: "Simple Formulations",
      body: "We believe supplements should be straightforward and easy to understand. EL Well focuses on carefully selected active ingredients, clear formulations and nothing unnecessary.",
    },
    {
      number: "02",
      title: "Transparent Ingredients",
      body: "We believe in clear, transparent labelling. Active ingredients are clearly identified, with no proprietary blends hiding what matters.",
    },
    {
      number: "03",
      title: "Quality-Focused",
      body: "We focus on clearly specified ingredients, transparent labelling and quality-controlled manufacturing from sourcing through to the finished product.",
    },
  ],
};

export const qualityCommitments = [
  {
    title: "Full label transparency",
    body: "Every ingredient and every amount, printed clearly. If it is in the bottle, it is on the label.",
  },
  {
    title: "Short ingredient lists",
    body: "We formulate with as few ingredients as the product allows. Nothing added to impress, nothing hidden to deceive.",
  },
  {
    title: "Batch traceability",
    body: "Every bottle can be traced back to its production batch.",
  },
  {
    title: "Honest claims only",
    body: "We will only ever publish health claims that are authorised for use in Great Britain, worded exactly as approved.",
  },
];

// EDIT: as certifications or third-party testing are confirmed,
// publish them here. Nothing is listed until it is real.
export const qualityDisclosures = {
  title: "What we will publish here",
  body: "As EL Well grows, this page will carry our certificates, testing summaries and manufacturing standards — but only once they are real, current and verifiable. We would rather show you nothing than show you something we cannot prove.",
};

export const generalFaqs = [
  {
    q: "Why is it called Magnesium Glycinate?",
    a: "Magnesium bisglycinate is commonly known as magnesium glycinate. EL Well Nº 01 uses 100% magnesium bisglycinate chelate while using the familiar product name Magnesium Glycinate.",
  },
  {
    q: "What is Magnesium Bisglycinate?",
    a: "Magnesium bisglycinate is magnesium bound to the amino acid glycine — a fully reacted chelated form used in EL Well Nº 01.",
  },
  {
    q: "Why did EL Well choose this form?",
    a: "Because it's fully reacted, non-buffered, clearly identified and contains no magnesium oxide.",
  },
  {
    q: "Why choose EL Well?",
    a: "We create simple, transparent supplements with carefully selected ingredients and quality you can trust.",
  },
  {
    q: "What makes EL Well different?",
    a: "One clearly identified active ingredient. Complete label transparency. No unnecessary ingredients. Nothing hidden.",
  },
  {
    q: "Do your products contain magnesium oxide?",
    a: "No. EL Well Nº 01 contains 100% magnesium bisglycinate chelate and no magnesium oxide.",
  },
  {
    q: "Where are your products made?",
    a: "Our products are manufactured in the UK by trusted manufacturing partners to recognised quality standards.",
  },
  {
    q: "Where is EL Well based?",
    a: "EL Well is a UK wellness brand serving customers across the United Kingdom.",
  },
  {
    q: "When will Magnesium Glycinate be available?",
    a: "We're preparing for launch. Join our waiting list and we'll let you know as soon as EL Well Nº 01 becomes available.",
  },
  {
    q: "How much does shipping cost?",
    a: "Shipping costs are shown at checkout. Full details are available on our Shipping & Returns page.",
  },
  {
    q: "Can I return a product?",
    a: "Yes. Unopened products can be returned in accordance with our Shipping & Returns Policy and UK consumer rights.",
  },
  {
    q: "Will EL Well launch more products?",
    a: "Yes. Magnesium Glycinate is EL Well Nº 01, with more carefully formulated products to follow.",
  },
  {
    q: "Are your products medicines?",
    a: "No. EL Well products are food supplements and are not intended to diagnose, treat, cure or prevent any disease.",
  },
];

// EDIT: final delivery options and prices before launch
export const shipping = {
  options: [
    { name: "Standard UK Delivery", detail: "Timeframe confirmed before orders open", price: "Confirmed before orders open" },
    { name: "Express UK Delivery", detail: "Timeframe confirmed before orders open", price: "Confirmed before orders open" },
  ],
  dispatch: "Orders are prepared and dispatched from the UK. Dispatch timescales will be confirmed at launch.",
  returns:
    "Under the UK Consumer Contracts Regulations you have 14 days from delivery to change your mind about unopened products. To start a return, contact us with your order number and we will guide you through it. Refunds are issued to your original payment method once we receive the returned item.",
};

export const legal = {
  company: "EL Well Wellness Ltd",
  companyNumber: "17451661",
  registeredOffice: "10 Beechcroft View, Leeds, LS11 0LN",
  email: "hello@elwellnutrition.co.uk",
  updated: "Last updated: September 2026",
};

export const whyChoose = {
  title: "Why Choose EL Well?",
  intro:
    "At EL Well, we believe wellness should be simple, honest and built on quality. That belief shapes everything we make — from the nutrients we choose to the words we print on the label.",
  features: [
    "Ingredients Without Compromise",
    "Science-Informed Formulations",
    "Clear, Honest Labelling",
    "A UK Brand You Can Trust",
    "Everyday Wellbeing",
  ],
};

export const featuredProduct = {
  title: "EL Well Magnesium Glycinate",
  body: "Experience premium magnesium supplementation with our carefully formulated Magnesium Glycinate, made using 100% Magnesium Bisglycinate Chelate. Designed to support your everyday wellbeing with a clean, transparent formulation you can trust.",
};

export const whyFormula = {
  title: "Why Our Formula?",
  cards: [
    { title: "100% Magnesium Bisglycinate Chelate", body: "A premium, fully reacted chelated form of magnesium." },
    { title: "Non-Buffered Formula", body: "No magnesium oxide added." },
    { title: "Transparent Formulation", body: "Nothing unnecessary. Nothing hidden." },
    { title: "Vegan Capsules", body: "Suitable for a plant-based lifestyle." },
  ],
};

export const aboutHome = {
  eyebrow: "About EL Well",
  paragraphs: [
    "EL Well is a UK wellness brand built on a simple belief: good health should be easy to understand.",
    "We make a small number of supplements exceptionally well, starting with Nº 01 — and we tell you exactly what is inside every bottle, and why it is there.",
  ],
};

export const moreThan = {
  title: "More Than a Wellness Brand",
  paragraphs: [
    "Wellness is not only what we put into our bodies — it is how we look after each other. That belief shapes our products, our words and the partnerships we choose.",
  ],
  cta: "Learn Why EL Well →",
};

export const nourish = {
  title: "Every Purchase Makes a Difference",
  lead: "Every purchase helps support the Nourish Fund and its mission to improve lives through better nutrition and community support.",
  body: "By choosing EL Well, you're supporting a brand that believes wellness extends beyond our products and into the wider world.",
};

export const aboutPage = {
  vision: {
    title: "Our Vision",
    body: "To become one of the UK's most trusted wellness brands, empowering people to live healthier lives through simple, premium-quality nutrition.",
  },
  mission: {
    title: "Our Mission",
    body: "Our mission is to create premium, science-informed supplements using carefully selected, high-quality ingredients that people can trust. We are committed to keeping wellness simple through transparent formulations, honest communication and products designed to support everyday wellbeing.",
  },
  promise: "Wellness made simple through quality you can trust.",
  values: [
    { title: "Quality", body: "We never compromise on ingredient quality." },
    { title: "Transparency", body: "We clearly communicate what is in every product." },
    { title: "Simplicity", body: "We believe wellness should be easy to understand and easy to maintain." },
    { title: "Trust", body: "We build long-term relationships through honesty, consistency and quality." },
    {
      title: "Purpose",
      body: "We create products that genuinely support everyday wellbeing while making a positive impact through our partnership with the Nourish Fund.",
    },
  ],
};
